import styled, { css } from "styled-components";
import Sortable, { MultiDrag, SortableEvent } from "sortablejs";
import { Checked } from "../FormFields/CheckboxField";
import assetsConfig from "../../assetsConfig";

Sortable.mount(new MultiDrag());

export const DragCard = styled.div<{
	className?: string;
	hideOnDrag?: boolean;
	disabled?: boolean;
}>`
	position: relative;
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 1rem;
	margin: 1px;
	outline: 1px solid ${({ theme }) => theme.colorBorder};
	background: ${({ theme }) => theme.colorBackgroundLight};
	/* transition: all 150ms ease;
	max-height: 100px; */
	${(p) =>
		!p.disabled &&
		css`
			cursor: pointer;
		`}

	&.sortable-selected {
		background: ${({ theme }) => theme.colorSelected};

		.checkbox-field {
			${Checked};
		}
	}

	&.sortable-ghost {
		background: ${({ theme }) => theme.colorAwardThree};
	}

	.drag-icons {
		display: flex;
		align-items: center;
		width: 100%;

		.drag-arrows {
			margin-right: 1.25rem;
		}
	}

	${(p) =>
		p.hideOnDrag &&
		css`
			display: none;
		`};

	.collapsible {
		display: none;
	}

	.details {
		flex: 1;
	}

	.triangle,
	.caret-down,
	.close-icon {
		display: none;
		cursor: pointer;
	}

	.icon-container {
		display: none;
	}

	.checkbox-field {
		display: flex;
		justify-content: center;
		margin-left: auto;
		input {
			margin-right: 0;
		}
	}

	p {
		max-width: 130px;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		line-height: 1rem;

		&.media-type,
		&.upload-date {
			font-size: ${({ theme }) => theme.xxSmallSize};
		}

		&.media-type {
			width: 110px;
		}

		&.media-file-name {
			max-width: 300px;
			font-size: ${({ theme }) => theme.xSmallSize};
			font-weight: ${({ theme }) => theme.fontSemiBold};
			white-space: pre-wrap;
			overflow-wrap: anywhere;
		}
	}

	.light {
		color: ${({ theme }) => theme.colorCopyLight};
	}

	@media only screen and (min-width: 1050px) {
		p {
			max-width: 140px;
		}
	}

	@media only screen and (min-width: 1100px) {
		p {
			max-width: 160px;
		}
	}

	@media only screen and (min-width: ${({ theme }) => theme.xl}) {
		p {
			max-width: 190px;
		}
	}

	@media only screen and (min-width: ${({ theme }) => theme.xxl}) {
		p {
			max-width: 225px;
		}
	}

	// css only for credit drag
	&.credit-drag {
		p {
			font-size: 0.875rem;
			line-height: 1.15rem;
		}
		.credits {
			display: flex;
			justify-content: space-between;
			align-items: center;

			.credit-fields {
				flex: 1;
			}
		}

		.drag-icons {
			width: initial;
		}
	}

	.multi-drag-count-badge {
		position: absolute;
		top: -6px;
		right: -6px;
		width: 28px;
		height: 28px;
		padding: 0 0.4rem;
		box-sizing: border-box;
		border-radius: 9999px;
		background: ${({ theme }) => theme.colorPrimary};
		color: ${({ theme }) => theme.colorBackgroundLightLight};
		font-size: 0.75rem;
		font-weight: 600;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 5;
		pointer-events: none;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
	}
`;

export const DropZone = styled.div<{
	isInDropzone: boolean; // default = true, cards in dropzone can be expanded, downloaded, etc
	maxHeight?: string;
	isError?: boolean;
	success?: boolean;
	disabled?: boolean;
	hideShadow?: boolean;
}>`
	display: flex;
	flex-direction: column;
	position: relative;
	width: 100%;
	height: 100%;
	min-height: 194px; // 1 card plus border
	background: ${({ theme }) => theme.colorBackgroundLightLight};

	transition: max-height 0.15s ease;

	${(p) =>
		!p.hideShadow &&
		css`
			box-shadow: 0 2px 8px 0 ${({ theme }) => theme.colorBoxShadow};
		`};

	${(p) =>
		p.disabled &&
		css`
			background: ${({ theme }) => theme.colorFieldReadOnly};
		`};

	${(p) =>
		p.maxHeight &&
		css`
			max-height: ${p.maxHeight};
		`};

	${(p) =>
		p.isError &&
		css`
			border: 1px dashed ${({ theme }) => theme.colorDanger};
		`};

	${(p) =>
		p.success &&
		css`
			border: 1px dashed ${({ theme }) => theme.colorSuccess};
		`};

	${(p) =>
		p.isInDropzone &&
		css`
			.draggable {
				/* cursor: default; */

				// only apply for draggable media, not credits
				&.drag-media {
					flex-direction: column;
					justify-content: flex-start;
					align-items: flex-start;

					.drag-arrows {
						display: none;
					}
				}

				.collapsible {
					display: flex;
					gap: 1rem;
				}

				.details {
					justify-content: flex-start;
					gap: 1rem;
					flex: 1;
					width: 100% !important;
				}

				.media-file-name,
				.field p {
					max-width: unset;
				}

				.field p {
					white-space: pre-wrap;
				}

				.caret-down,
				.close-icon {
					display: flex;
				}

				.checkbox-field {
					display: none;
				}

				.icon-container {
					display: flex;
					justify-content: end;
					align-items: center;
					gap: 0.5rem;
				}
			}
		`}
`;

export const filterDuplicates = <T extends DragItem>(arr: T[]): T[] => {
	const ids = arr.map(({ id }) => id);
	const filtered = arr.filter(({ id }, index) => !ids.includes(id, index + 1));

	return filtered;
};

export const checkMax = <T extends DragItem>(
	newCredit: T[],
	max?: number,
): boolean => {
	// check if dropping a new drag item would exceed the file limit
	const wouldMaxDropped =
		max !== undefined && max !== 0
			? newCredit.length !== 0 && newCredit.length > max
			: false;

	return wouldMaxDropped;
};

const MULTI_DRAG_COUNT_BADGE_CLASS = "multi-drag-count-badge";

/** Selected count in a list root, excluding Sortable's ghost/clone (cloneNode copies keep .sortable-selected). */
function countSortableMultiSelected(from: HTMLElement): number {
	const ghost = Sortable.ghost;
	const clone = Sortable.clone;
	return Array.from(from.querySelectorAll(".sortable-selected")).filter(
		(el) => {
			const n = el as HTMLElement;
			if (ghost && (n === ghost || ghost.contains(n))) return false;
			if (clone && (n === clone || clone.contains(n))) return false;
			return true;
		},
	).length;
}

export const sortableOptions = (props: SortableOptionsProps) => ({
	group: {
		name: props.group,

		// pull: ability to move from the list.
		...(props.clone && ({ pull: "clone" } as any)),

		// put: ability to add elements from other lists
		...(props.drop === false && ({ put: false } as any)),
	},
	sort: props.sortable === false ? false : true,
	multiDrag: props.allowMultiSelect,
	// Native HTML5 drag does not create Sortable.ghost; we need the ghost for the multi-count badge on clone drags.
	...(props.clone &&
		props.allowMultiSelect && {
			forceFallback: true,
			avoidImplicitDeselect: true,
			fallbackTolerance: 3, // Allow for more flexible dragging tolerance (important for macOS)
		}),

	animation: 150,
	easing: "ease",

	selectedClass: "sortable-selected", // Class name for selected item
	ghostClass: "sortable-ghost", // Class name for the drop placeholder
	chosenClass: "sortable-chosen", // Class name for the chosen item
	dragClass: "sortable-drag", // Class name for the dragging item

	draggable: ".draggable", // only allow elements with this class to be dragged
	// preventOnFilter: true, // Call `event.preventDefault()` when triggered `filter`

	onStart: (evt: SortableEvent) => {
		// Prevent text selection while dragging with forceFallback
		document.body.style.userSelect = "none";

		// No badge on clone sources (library) while still in the sidebar; see onMove.
		if (!props.allowMultiSelect || props.clone) return;
		const n = countSortableMultiSelected(evt.from as HTMLElement);
		if (n <= 1) return;
		const el = evt.item as HTMLElement;
		const badge = document.createElement("span");
		badge.className = MULTI_DRAG_COUNT_BADGE_CLASS;
		badge.setAttribute("aria-label", `${n} items selected`);
		badge.textContent = String(n);
		el.appendChild(badge);
	},

	onMove: (e: any) => {
		// Clone mode: evt.dragged is the original row in the library, not the floating
		// preview. Put the badge on Sortable.ghost so it never appears on the sidebar row.
		if (props.clone && props.allowMultiSelect) {
			const from = e.from as HTMLElement;
			const to = e.to as HTMLElement;
			const dragged = e.dragged as HTMLElement | undefined;
			dragged?.querySelector(`.${MULTI_DRAG_COUNT_BADGE_CLASS}`)?.remove();

			const ghost = Sortable.ghost;
			if (from && to && ghost) {
				if (from !== to) {
					const n = countSortableMultiSelected(from);
					if (
						n > 1 &&
						!ghost.querySelector(`.${MULTI_DRAG_COUNT_BADGE_CLASS}`)
					) {
						const badge = document.createElement("span");
						badge.className = MULTI_DRAG_COUNT_BADGE_CLASS;
						badge.setAttribute("aria-label", `${n} items selected`);
						badge.textContent = String(n);
						ghost.appendChild(badge);
					}
				} else {
					ghost.querySelector(`.${MULTI_DRAG_COUNT_BADGE_CLASS}`)?.remove();
				}
			}
		}

		// hide any duplicates when cloning
		if (props.clone) {
			Array.from(e.from.children as HTMLCollectionOf<HTMLElement>).forEach(
				(draggable) => {
					if (
						draggable.classList.contains("sortable-selected") &&
						!draggable.classList.contains("sortable-chosen")
					) {
						draggable.style.display = "none";
					}
				},
			);
		}

		// prevent element with .no-drag to be moved
		return e.related.className.indexOf("no-drag") === -1;
	},

	onEnd: (e: any) => {
		document.body.style.userSelect = "";

		const item = e.item as HTMLElement;
		const dragged = e.dragged as HTMLElement | undefined;
		item?.querySelector?.(`.${MULTI_DRAG_COUNT_BADGE_CLASS}`)?.remove();
		if (dragged && dragged !== item) {
			dragged.querySelector?.(`.${MULTI_DRAG_COUNT_BADGE_CLASS}`)?.remove();
		}
		Sortable.ghost
			?.querySelector?.(`.${MULTI_DRAG_COUNT_BADGE_CLASS}`)
			?.remove();

		// show all cards after dnd is complete
		Array.from(e.from.children as HTMLCollectionOf<HTMLElement>).forEach(
			(draggable) => {
				draggable.style.display = "flex";
			},
		);

		// 	deselect items after moving to another list
		for (let i in e.items) {
			Sortable.utils.deselect(e.items[i]);
		}
	},
});

export interface DragItem {
	id: any;
}

export interface DraggableProps {
	name: string; // field name for formik, must be unqiue
	clone?: boolean; // copy the item rather than move
	drop?: boolean; // disables dropping to the list
	sortable?: boolean; // default = true, allow weight change
	allowMultiSelect?: boolean; // default = false
}

interface SortableOptionsProps extends DraggableProps {
	group: string;
}
